import createSearch from "MODULES/shared/actions/search";
import { SUCCESS } from "LIB/submission/constants";
import { PO_ITEM, RO_ITEM, SO_ITEM } from "MODULES/Orders/shared/constants";
import { get, isEmpty } from "lodash";
import api from "APP_ROOT/api";
import { buildFilters } from "MODULES/Shipping/actions";
import { createChannel } from "APP_ROOT/cable";
import { EXPECTED_SERIAL_NUMBER } from "MODULES/PurchaseOrders/constants";
import {
  receivingDashboardOrders,
  virtualReceivingDashboardOrders
} from "MODULES/Receiving/Dashboard/pagination";
import { SHIPPING_TYPE } from "APP_ROOT/constants";
import { TENANT_LOCATION } from "MODULES/shared/components/ShippingSection/shippingTypes";
import { useCurrentUser } from "APP_ROOT/hooks";
import { REPAIR_EVALUATION_APPROVED } from "../RepairOrders/constants";
import * as actionTypes from "./actionTypes";
import {
  CONDITION_DISCREPANCY,
  DISCREPANCY,
  EMA,
  PART_NUMBER_DISCREPANCY,
  SERIAL_NUMBER_DISCREPANCY,
  MISTAKE_SHIPMENT,
  TAGGED_BY_DISCREPANCY,
  RECEIVING_STORE_INCORRECT
} from "./constants";
import { receivingRecordsSearch } from "./pagination";
import {
  CONDITION,
  PART_ID,
  SERIAL_NUMBER,
  TAGGED_BY
} from "../inventories/constants";

export const { pageActions, stateLocator } = receivingRecordsSearch;

export const { pageActions: discrepanciesPageActions } = createSearch(
  "discrepancies"
);

export function show(id) {
  return dispatch =>
    api.receivingRecord.show(id).then(resp =>
      dispatch({
        type: actionTypes.SET,
        receivingRecord: resp.data
      })
    );
}

export function update(data) {
  return (dispatch, getState) => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    return api.receivingRecord.update(receivingRecordID, data).then(resp => {
      dispatch({
        type: actionTypes.SET,
        receivingRecord: resp.data
      });
      return SUCCESS;
    });
  };
}

export function updateQCStatus(data) {
  return (dispatch, getState) => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    return api.receivingRecord
      .updateQcStatus(receivingRecordID, data)
      .then(resp => {
        dispatch({
          type: actionTypes.SET,
          receivingRecord: resp.data
        });

        return SUCCESS;
      });
  };
}

function buildAttachmentFiles(attachment) {
  const fileName = `${+new Date() -
    Math.floor(Math.random() * 10000000000)}${attachment.file.name}`;
  return api.attachments.upload(fileName).then(resp => {
    const { key, url } = resp.data;
    const formattedAttachment = {
      attachment_type: attachment.attachment_type,
      name: fileName,
      key
    };
    return api.attachments
      .toS3(url, attachment.file)
      .then(() => Promise.resolve(formattedAttachment))
      .catch(e => Promise.reject(e));
  });
}

function buildDiscrepancyFiles(file) {
  const fileName = `${+new Date() -
    Math.floor(Math.random() * 10000000000)}${file.name}`;
  return api.attachments.upload(fileName).then(resp => {
    const { key, url } = resp.data;
    const formattedAttachment = {
      attachment_type: DISCREPANCY,
      name: fileName,
      key
    };
    return api.attachments
      .toS3(url, file)
      .then(() => Promise.resolve(formattedAttachment))
      .catch(e => Promise.reject(e));
  });
}

export function buildAttachments(attachment = {}) {
  if (attachment.uploaded) {
    return Promise.resolve(attachment);
  }

  return new Promise((resolve, _) =>
    buildAttachmentFiles(attachment)
      .then(file => {
        resolve(file);
      })
      .catch(e => Promise.reject(e))
  );
}

const discrepancyMap = {
  part_id: {
    discrepancy_type: PART_NUMBER_DISCREPANCY
  },
  [CONDITION]: {
    discrepancy_type: CONDITION_DISCREPANCY
  },
  [SERIAL_NUMBER]: {
    discrepancy_type: SERIAL_NUMBER_DISCREPANCY
  },
  [TAGGED_BY]: {
    discrepancy_type: TAGGED_BY_DISCREPANCY
  },
  [SHIPPING_TYPE]: {
    discrepancy_type: RECEIVING_STORE_INCORRECT
  }
};

function isDiscrepancy(key, formData, lineItem) {
  if (!lineItem) return null;
  const type = lineItem && lineItem.type;
  if ((key === CONDITION || key === SERIAL_NUMBER) && type === SO_ITEM) {
    return false;
  }

  if (key === SHIPPING_TYPE) {
    if (lineItem[SHIPPING_TYPE] !== TENANT_LOCATION) return false;
    return (
      get(lineItem, "ship_to_location.id") !== useCurrentUser().location_id
    );
  }

  if (key === TAGGED_BY && type !== PO_ITEM) {
    return false;
  }

  if (key === SERIAL_NUMBER && type === "Rma") {
    return false;
  }

  const defaultValue = lineItem[key];

  if (key === PART_ID && lineItem.type === RO_ITEM) {
    if (
      lineItem.approval_status === REPAIR_EVALUATION_APPROVED &&
      lineItem.mod_to_id
    ) {
      return formData.part_id !== lineItem.mod_to_id;
    }

    return formData.part_id !== lineItem.part_id;
  }

  if (lineItem.type === PO_ITEM) {
    if (key === PART_ID) {
      return ![
        lineItem.part_id,
        get(lineItem, "alternate.alternate_part_id"),
        lineItem.mod_to_id
      ].includes(formData.part_id);
    } else if (key === SERIAL_NUMBER) {
      if (!isEmpty(lineItem[EXPECTED_SERIAL_NUMBER])) {
        return formData.serial_number !== lineItem[EXPECTED_SERIAL_NUMBER];
      }
      return false;
    }
  }

  if (
    [PART_ID, SERIAL_NUMBER].includes(key) &&
    lineItem.type === RO_ITEM &&
    lineItem.mod_to_id === formData.part_id
  ) {
    return false;
  }

  return lineItem[key] && formData[key] !== defaultValue;
}

function buildAutomaticDiscrepancies(formData, lineItem = {}) {
  return [CONDITION, PART_ID, SERIAL_NUMBER, SHIPPING_TYPE, TAGGED_BY]
    .filter(key => isDiscrepancy(key, formData, lineItem))
    .map(type => Object.assign(discrepancyMap[type], { attachments: [] }));
}

export function save({ id, formData }) {
  const dash = formData.received_virtually
    ? virtualReceivingDashboardOrders
    : receivingDashboardOrders;

  return dispatch =>
    Promise.all(
      (formData.attachments || [])
        .map(attachment =>
          attachment.files.map(f => ({
            file: f,
            ...attachment
          }))
        )
        .flat()
        .map(buildAttachments)
    ).then(attachments => {
      const formattedData = Object.assign({}, formData, {
        attachments
      });
      return api.receivingRecord.save(id, formattedData).then(() => {
        dispatch(dash.pageActions.expire());
        return SUCCESS;
      });
    });
}

export function create({ formData, lineItem }) {
  return dispatch =>
    Promise.all(
      (formData.attachments || [])
        .map(attachment =>
          (attachment.files || [{}]).map(f => ({
            file: f,
            ...attachment
          }))
        )
        .flat()
        .map(buildAttachments)
    )
      .then(attachments => {
        const discrepancies = buildAutomaticDiscrepancies(formData, lineItem);
        const formattedData = Object.assign({}, formData, {
          attachments,
          discrepancies
        });

        return api.receivingRecord.create(formattedData).then(({ data }) => {
          dispatch({
            type: actionTypes.SET,
            receivingRecord: data
          });

          const anyCheckListFalse = [
            "shipped_properly",
            "vendor_packing_list_included",
            "packing_list_and_tags_match",
            "passed_damage_inspection",
            "release_certificate_included"
          ].some(f => !data[f]);

          if (
            (data.discrepancies.length > 0 ||
              ((!data.received_virtually ||
                data.reference_record_line_item_type ===
                  "InventoryTransferOrder") &&
                anyCheckListFalse)) &&
            ![EMA, MISTAKE_SHIPMENT].includes(formData.receiving_type)
          ) {
            dispatch({
              type: actionTypes.SET_DISCREPANCIES,
              discrepancies: data.discrepancies
            });
            dispatch({ type: actionTypes.SET_MODAL, showModal: true });
          }
          if (formData.reported) {
            dispatch({ type: actionTypes.SET_MODAL, showModal: true });
          }
          return SUCCESS;
        });
      })
      .catch(e => Promise.reject(e));
}

function buildDiscrepancies(discrepancy) {
  return new Promise((resolve, _) => {
    if (!discrepancy.files) {
      resolve(Object.assign({}, discrepancy, { attachments: [] }));
    } else {
      Promise.all(discrepancy.files.map(buildDiscrepancyFiles))
        .then(attachments => {
          resolve(Object.assign({}, discrepancy, { attachments }));
        })
        .catch(e => Promise.reject(e));
    }
  });
}

function refreshReceivingAndDiscrepancies(dispatch, receivingRecordId) {
  return api.receivingRecord.show(receivingRecordId).then(resp => {
    dispatch({
      type: actionTypes.SET,
      receivingRecord: resp.data
    });
    dispatch(discrepanciesPageActions.expire());
  });
}

export const resolveDiscrepancy = discrepancy => (dispatch, getState) =>
  api.discrepancy.resolve(discrepancy.id, discrepancy).then(() => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    refreshReceivingAndDiscrepancies(dispatch, receivingRecordID);
    return SUCCESS;
  });

export function resetDiscrepancies() {
  return dispatch => dispatch({ type: actionTypes.REMOVE_DISCREPANCIES });
}

export function createDiscrepancy(data) {
  return (dispatch, getState) => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    return Promise.all(
      data.discrepancies.map(buildDiscrepancies)
    ).then(discrepancies =>
      api.discrepancy
        .create(receivingRecordID, { discrepancies })
        .then(() => {
          refreshReceivingAndDiscrepancies(dispatch, receivingRecordID);
          resetDiscrepancies();
          return SUCCESS;
        })
        .catch(e => Promise.reject(e))
    );
  };
}

export function editDiscrepancy(discrepancy) {
  return (dispatch, getState) => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    return buildDiscrepancies(discrepancy).then(formattedDiscrepancy =>
      api.discrepancy
        .update(formattedDiscrepancy.id, { discrepancy: formattedDiscrepancy })
        .then(() => {
          refreshReceivingAndDiscrepancies(dispatch, receivingRecordID);
          return SUCCESS;
        })
        .catch(e => Promise.reject(e))
    );
  };
}

export function destroyAttachment(id) {
  return (dispatch, getState) => {
    const receivingRecordID = getState().receivingRecordDetail.getIn([
      "receivingRecord",
      "id"
    ]);
    return api.attachments.destroy(id).then(() => {
      refreshReceivingAndDiscrepancies(dispatch, receivingRecordID);
      return SUCCESS;
    });
  };
}

let channel = null;

export function unsubscribe() {
  return () => {
    if (channel) {
      channel.unsubscribe();
      channel = null;
    }
  };
}

export function reloadSilent() {
  return (dispatch, getState) => {
    const currentState = getState();
    const paginator = stateLocator(currentState);
    const filters = buildFilters(paginator);

    return api.receivingRecord.search(filters).then(({ data }) => {
      dispatch(pageActions.resetResults(data.results));
    });
  };
}

export function onDisconnect() {
  return unsubscribe();
}

export function subscribe(onReceive = reloadSilent) {
  return dispatch => {
    if (!channel) {
      const { listen } = createChannel();

      channel = listen("ReceivingRecordsChannel", {
        onDisconnect,
        onReceive: data => {
          if (["CREATE"].includes(data.action)) {
            dispatch(onReceive(data));
          }
        }
      });
    }
  };
}



// WEBPACK FOOTER //
// ./app/modules/Receiving/actions.js